import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAdminAuth } from '../context/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const slugify = (str) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const formatLocalDate = () => {
  const d = new Date();
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// ─── Quill toolbar config ─────────────────────────────────────────────────────

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'blockquote', 'list', 'bullet', 'link',
];

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #0d0d0d;
  padding-bottom: 80px;
`;

const TopBar = styled.header`
  background: #111;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
`;

const Brand = styled.div`
  font-size: 1.15rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.3px;
  span { color: #fe88f1; }
`;

const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AdminBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #fe88f1;
  background: rgba(254,136,241,0.1);
  padding: 4px 10px;
  border-radius: 4px;
`;

const LogoutBtn = styled.button`
  padding: 8px 16px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 6px;
  color: rgba(255,255,255,0.7);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: rgba(255,255,255,0.1); }
`;

const Content = styled.main`
  max-width: 1100px;
  margin: 0 auto;
  padding: 40px 24px 0;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// ─── Editor Panel ─────────────────────────────────────────────────────────────

const Panel = styled.section`
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 14px;
  padding: 28px 28px 32px;
`;

const PanelTitle = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 24px;
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    font-size: 0.72rem;
    font-weight: 600;
    color: rgba(255,255,255,0.35);
    background: rgba(255,255,255,0.06);
    padding: 2px 8px;
    border-radius: 99px;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.4);
  margin-bottom: 7px;
`;

const Input = styled.input`
  width: 100%;
  padding: 11px 14px;
  background: rgba(255,255,255,0.05);
  border: 1.5px solid rgba(255,255,255,0.09);
  border-radius: 8px;
  color: #fff;
  font-size: 0.92rem;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: rgba(254,136,241,0.45); }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const FieldGroup = styled.div`
  margin-bottom: 18px;
`;

const QuillWrapper = styled.div`
  .ql-toolbar {
    background: rgba(255,255,255,0.04);
    border: 1.5px solid rgba(255,255,255,0.09);
    border-bottom: 1px solid rgba(255,255,255,0.06);
    border-radius: 8px 8px 0 0;
  }
  .ql-container {
    background: rgba(255,255,255,0.03);
    border: 1.5px solid rgba(255,255,255,0.09);
    border-top: none;
    border-radius: 0 0 8px 8px;
    min-height: 280px;
    color: #e0e0e0;
    font-size: 0.95rem;
  }
  .ql-editor { min-height: 280px; }
  .ql-picker, .ql-stroke { stroke: rgba(255,255,255,0.6) !important; }
  .ql-fill { fill: rgba(255,255,255,0.6) !important; }
  .ql-picker-label { color: rgba(255,255,255,0.6) !important; }
  .ql-toolbar button:hover .ql-stroke { stroke: #fe88f1 !important; }
  .ql-toolbar button.ql-active .ql-stroke { stroke: #fe88f1 !important; }
  .ql-toolbar button:hover .ql-fill { fill: #fe88f1 !important; }
  .ql-toolbar button.ql-active .ql-fill { fill: #fe88f1 !important; }
  .ql-editor.ql-blank::before { color: rgba(255,255,255,0.2); font-style: normal; }
  .ql-editor h2 { color: #fff; font-size: 1.3rem; margin: 20px 0 10px; }
  .ql-editor h3 { color: #fff; font-size: 1.1rem; margin: 16px 0 8px; }
  .ql-editor blockquote {
    border-left: 3px solid #fe88f1;
    color: rgba(255,255,255,0.6);
    padding-left: 16px;
    margin: 12px 0;
    font-style: italic;
  }
  .ql-editor a { color: #fe88f1; }
  .ql-picker-options { background: #1a1a1a !important; border-color: rgba(255,255,255,0.1) !important; }
  .ql-picker-item { color: rgba(255,255,255,0.7) !important; }
  .ql-picker-item:hover { color: #fe88f1 !important; }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;

  input { opacity: 0; width: 0; height: 0; }

  span {
    position: absolute;
    inset: 0;
    background: rgba(255,255,255,0.1);
    border-radius: 22px;
    cursor: pointer;
    transition: background 0.2s;
    &::before {
      content: '';
      position: absolute;
      width: 16px; height: 16px;
      left: 3px; top: 3px;
      background: #fff;
      border-radius: 50%;
      transition: transform 0.2s;
    }
  }

  input:checked + span { background: #fe88f1; }
  input:checked + span::before { transform: translateX(18px); }
`;

const ToggleLabel = styled.span`
  font-size: 0.88rem;
  color: rgba(255,255,255,0.55);
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const PrimaryBtn = styled.button`
  flex: 1;
  padding: 13px;
  background: #fe88f1;
  color: #0d0d0d;
  border: none;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const SecondaryBtn = styled.button`
  padding: 13px 20px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: rgba(255,255,255,0.6);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: rgba(255,255,255,0.1); }
`;

const FeedbackMsg = styled.p`
  font-size: 0.85rem;
  margin: 12px 0 0;
  color: ${(p) => (p.$error ? '#ff7e7e' : '#7effb2')};
  text-align: center;
`;

// ─── Post List ────────────────────────────────────────────────────────────────

const PostList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PostItem = styled.li`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 10px;
  padding: 14px 16px;
`;

const PostMeta = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
`;

const PostBadge = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 3px 7px;
  border-radius: 4px;
  background: ${(p) => (p.$published ? 'rgba(126,255,178,0.15)' : 'rgba(255,255,255,0.08)')};
  color: ${(p) => (p.$published ? '#7effb2' : 'rgba(255,255,255,0.4)')};
  flex-shrink: 0;
  margin-top: 2px;
`;

const PostTitle = styled.p`
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
  margin: 0;
  line-height: 1.35;
  flex: 1;
`;

const PostActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionBtn = styled.button`
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid;
  transition: opacity 0.2s;

  ${(p) =>
    p.$variant === 'edit'
      ? `background: rgba(254,136,241,0.1); color: #fe88f1; border-color: rgba(254,136,241,0.25);`
      : `background: rgba(255,126,126,0.1); color: #ff7e7e; border-color: rgba(255,126,126,0.25);`}

  &:hover { opacity: 0.75; }
`;

const EmptyState = styled.p`
  font-size: 0.88rem;
  color: rgba(255,255,255,0.3);
  text-align: center;
  padding: 24px 0;
`;

const LoadingSpinner = styled.div`
  border: 2px solid rgba(254,136,241,0.15);
  border-top-color: #fe88f1;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 0.8s linear infinite;
  margin: 20px auto;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ─── Main Component ───────────────────────────────────────────────────────────

const EMPTY_FORM = {
  title: '',
  subtitle: '',
  slug: '',
  category: 'Film Analysis',
  coverImage: '',
  author: 'MyCinemaDB Team',
  date: '',
  published: false,
};

const AdminDashboard = () => {
  const { isAdmin, adminToken, logout } = useAdminAuth();
  const navigate = useNavigate();

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) navigate('/admin/login', { replace: true });
  }, [isAdmin, navigate]);

  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [content, setContent] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ msg: '', error: false });

  const authHeader = { Authorization: `Bearer ${adminToken}` };

  const fetchPosts = useCallback(async () => {
    setLoadingPosts(true);
    try {
      const res = await fetch(`${BACKEND_URL}/admin/posts`, { headers: authHeader });
      if (!res.ok) throw new Error();
      setPosts(await res.json());
    } catch {
      // silently fail
    } finally {
      setLoadingPosts(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const showFeedback = (msg, error = false) => {
    setFeedback({ msg, error });
    setTimeout(() => setFeedback({ msg: '', error: false }), 3500);
  };

  const handleField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Auto-generate slug from title when not editing
      if (key === 'title' && !editingId) next.slug = slugify(value);
      return next;
    });
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setContent('');
    setEditingId(null);
  };

  const handleEdit = (post) => {
    setEditingId(post._id);
    setForm({
      title: post.title || '',
      subtitle: post.subtitle || '',
      slug: post.slug || '',
      category: post.category || 'Film Analysis',
      coverImage: post.coverImage || '',
      author: post.author || 'MyCinemaDB Team',
      date: post.date || '',
      published: post.published || false,
    });
    setContent(post.content || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/admin/posts/${id}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      if (!res.ok) throw new Error();
      showFeedback('Post deleted.');
      fetchPosts();
    } catch {
      showFeedback('Failed to delete post.', true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content || content === '<p><br></p>') {
      showFeedback('Post content cannot be empty.', true);
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      content,
      date: form.date || formatLocalDate(),
    };
    try {
      const url = editingId
        ? `${BACKEND_URL}/admin/posts/${editingId}`
        : `${BACKEND_URL}/admin/posts`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      // Server may have auto-changed the slug to avoid a collision
      if (data._slugChanged) {
        showFeedback(
          `Post created! Slug was already taken — saved as "${data.slug}" instead.`,
          false
        );
      } else {
        showFeedback(editingId ? 'Post updated!' : 'Post created!');
      }
      resetForm();
      fetchPosts();
    } catch (err) {
      showFeedback(err.message, true);
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <PageWrapper>
      <Helmet>
        <title>Admin Dashboard — MyCinemaDB</title>
      </Helmet>

      <TopBar>
        <Brand>My<span>Cinema</span>DB</Brand>
        <TopActions>
          <AdminBadge>Admin</AdminBadge>
          <LogoutBtn onClick={logout}>Sign out</LogoutBtn>
        </TopActions>
      </TopBar>

      <Content>
        <Layout>
          {/* ── Editor Panel ── */}
          <Panel>
            <PanelTitle>
              {editingId ? '✏️ Edit Post' : '✍️ New Post'}
            </PanelTitle>

            <form onSubmit={handleSubmit}>
              <FieldGroup>
                <FieldLabel htmlFor="d-title">Title *</FieldLabel>
                <Input
                  id="d-title"
                  value={form.title}
                  onChange={(e) => handleField('title', e.target.value)}
                  placeholder="The Machine That Forgot How to Be Wrong"
                  required
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="d-subtitle">Subtitle</FieldLabel>
                <Input
                  id="d-subtitle"
                  value={form.subtitle}
                  onChange={(e) => handleField('subtitle', e.target.value)}
                  placeholder="What Mercy gets right — and chillingly wrong — about algorithmic justice"
                />
              </FieldGroup>

              <Row>
                <FieldGroup>
                  <FieldLabel htmlFor="d-slug">Slug *</FieldLabel>
                  <Input
                    id="d-slug"
                    value={form.slug}
                    onChange={(e) => handleField('slug', slugify(e.target.value))}
                    placeholder="my-post-title"
                    required
                  />
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel htmlFor="d-category">Category</FieldLabel>
                  <Input
                    id="d-category"
                    value={form.category}
                    onChange={(e) => handleField('category', e.target.value)}
                    placeholder="Film Analysis"
                  />
                </FieldGroup>
              </Row>

              <Row>
                <FieldGroup>
                  <FieldLabel htmlFor="d-author">Author</FieldLabel>
                  <Input
                    id="d-author"
                    value={form.author}
                    onChange={(e) => handleField('author', e.target.value)}
                    placeholder="MyCinemaDB Team"
                  />
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel htmlFor="d-date">Date</FieldLabel>
                  <Input
                    id="d-date"
                    value={form.date}
                    onChange={(e) => handleField('date', e.target.value)}
                    placeholder="April 1, 2026 (auto-fills)"
                  />
                </FieldGroup>
              </Row>

              <FieldGroup>
                <FieldLabel htmlFor="d-cover">Cover Image URL</FieldLabel>
                <Input
                  id="d-cover"
                  value={form.coverImage}
                  onChange={(e) => handleField('coverImage', e.target.value)}
                  placeholder="https://images.unsplash.com/..."
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Content *</FieldLabel>
                <QuillWrapper>
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    placeholder="Write your article here…"
                  />
                </QuillWrapper>
              </FieldGroup>

              <ToggleRow>
                <Toggle>
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => handleField('published', e.target.checked)}
                  />
                  <span />
                </Toggle>
                <ToggleLabel>
                  {form.published ? 'Published — visible on /blogs' : 'Draft — hidden from public'}
                </ToggleLabel>
              </ToggleRow>

              <FormActions>
                <PrimaryBtn type="submit" disabled={saving}>
                  {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Publish Post'}
                </PrimaryBtn>
                {editingId && (
                  <SecondaryBtn type="button" onClick={resetForm}>
                    Cancel
                  </SecondaryBtn>
                )}
              </FormActions>

              {feedback.msg && (
                <FeedbackMsg $error={feedback.error}>{feedback.msg}</FeedbackMsg>
              )}
            </form>
          </Panel>

          {/* ── Post List Panel ── */}
          <Panel>
            <PanelTitle>
              Posts <span>{posts.length}</span>
            </PanelTitle>

            {loadingPosts ? (
              <LoadingSpinner />
            ) : posts.length === 0 ? (
              <EmptyState>No posts yet. Create your first one!</EmptyState>
            ) : (
              <PostList>
                {posts.map((p) => (
                  <PostItem key={p._id}>
                    <PostMeta>
                      <PostBadge $published={p.published}>
                        {p.published ? 'Live' : 'Draft'}
                      </PostBadge>
                      <PostTitle>{p.title}</PostTitle>
                    </PostMeta>
                    <PostActions>
                      <ActionBtn $variant="edit" onClick={() => handleEdit(p)}>
                        Edit
                      </ActionBtn>
                      <ActionBtn $variant="delete" onClick={() => handleDelete(p._id)}>
                        Delete
                      </ActionBtn>
                    </PostActions>
                  </PostItem>
                ))}
              </PostList>
            )}
          </Panel>
        </Layout>
      </Content>
    </PageWrapper>
  );
};

export default AdminDashboard;
